import { useMemo, useState } from 'react';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import FilterListIcon from '@mui/icons-material/FilterList';
import { Search } from '@/molecules/Search';
import { scrim } from '@/tokens/colors';
import type {
  FilterDef,
  FilterOption,
  FilterSelection,
  FilterValue,
} from '@/molecules/FilterMenu/types';

/**
 * FilterPopover
 * Classification: custom (composes Button + Popover + Search + Checkbox list)
 *
 * A popover-triggered filter editor built from a `filters` config. Supports
 * per-filter search, select-all, single/multi selection, apply/reset, a loading
 * state, optional async hooks, and `persistKey` (localStorage) persistence.
 *
 * Use this when several filter fields live behind one trigger and the user
 * should commit them together. For a single field behind a chip, use
 * `FilterMenu`; for an always-visible sidebar, `FilterPanel`.
 */

/**
 * The selection model is defined in `FilterMenu` and re-exported here, so this
 * organism's public API is unchanged by the move. See that file for why it
 * lives there.
 */
export type {
  FilterOption,
  FilterDef,
  FilterSelection,
  FilterValue,
} from '@/molecules/FilterMenu/types';

export interface FilterAsyncConfig {
  onSearch?: (key: string, query: string) => void;
  onLoadMore?: (key: string) => void;
  hasMore?: boolean;
}

export interface FilterPopoverProps {
  filters: FilterDef[];
  value?: FilterValue;
  defaultValue?: FilterValue;
  onApply?: (value: FilterValue) => void;
  onReset?: () => void;
  loading?: boolean;
  searchable?: boolean;
  showSelectAll?: boolean;
  /** localStorage key to persist the applied selection. */
  persistKey?: string;
  triggerLabel?: string;
  async?: FilterAsyncConfig;
}

const readPersisted = (key?: string): FilterValue | undefined => {
  if (!key || typeof window === 'undefined') return undefined;
  try {
    const raw = window.localStorage.getItem(`ds:filter:${key}`);
    return raw ? (JSON.parse(raw) as FilterValue) : undefined;
  } catch {
    return undefined;
  }
};

const writePersisted = (key: string | undefined, value: FilterValue) => {
  if (!key || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(`ds:filter:${key}`, JSON.stringify(value));
  } catch {
    /* ignore quota / serialization errors */
  }
};

const countActive = (value: FilterValue): number =>
  Object.values(value).filter((s) => s.values.length > 0).length;

export function FilterPopover({
  filters,
  value,
  defaultValue,
  onApply,
  onReset,
  loading = false,
  searchable = false,
  showSelectAll = false,
  persistKey,
  triggerLabel = 'Filters',
  async,
}: FilterPopoverProps) {
  const isControlled = value !== undefined;

  const initial = useMemo<FilterValue>(
    () => value ?? defaultValue ?? readPersisted(persistKey) ?? {},
    [value, defaultValue, persistKey],
  );

  const [applied, setApplied] = useState<FilterValue>(initial);
  const currentApplied = isControlled ? value! : applied;

  const [draft, setDraft] = useState<FilterValue>(initial);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [queries, setQueries] = useState<Record<string, string>>({});

  const activeCount = countActive(currentApplied);

  const open = (e: React.MouseEvent<HTMLElement>) => {
    setDraft(currentApplied);
    setAnchorEl(e.currentTarget);
  };
  const close = () => setAnchorEl(null);

  const getSelection = (key: string): FilterSelection => draft[key] ?? { operator: 'in', values: [] };

  const setSelection = (key: string, values: string[]) => {
    setDraft((prev) => ({ ...prev, [key]: { operator: prev[key]?.operator ?? 'in', values } }));
  };

  const toggleValue = (def: FilterDef, optionValue: string) => {
    const selected = getSelection(def.key).values;
    if (def.type === 'select') {
      setSelection(def.key, selected.includes(optionValue) ? [] : [optionValue]);
      return;
    }
    setSelection(
      def.key,
      selected.includes(optionValue)
        ? selected.filter((v) => v !== optionValue)
        : [...selected, optionValue],
    );
  };

  const toggleSelectAll = (def: FilterDef, visible: FilterOption[]) => {
    const selected = getSelection(def.key).values;
    const allSelected = visible.every((o) => selected.includes(o.value));
    setSelection(
      def.key,
      allSelected
        ? selected.filter((v) => !visible.some((o) => o.value === v))
        : Array.from(new Set([...selected, ...visible.map((o) => o.value)])),
    );
  };

  const apply = () => {
    if (!isControlled) setApplied(draft);
    writePersisted(persistKey, draft);
    onApply?.(draft);
    close();
  };

  const reset = () => {
    const empty: FilterValue = {};
    setDraft(empty);
    if (!isControlled) setApplied(empty);
    writePersisted(persistKey, empty);
    onReset?.();
  };

  const visibleOptions = (def: FilterDef): FilterOption[] => {
    const q = (queries[def.key] ?? '').trim().toLowerCase();
    if (!q) return def.options;
    return def.options.filter((o) => o.label.toLowerCase().includes(q));
  };

  return (
    <>
      <Badge badgeContent={activeCount} color="primary" invisible={activeCount === 0}>
        <Button
          variant="secondary"
          color="inherit"
          startIcon={<FilterListIcon fontSize="small" />}
          onClick={open}
          sx={{ textTransform: 'none' }}
        >
          {triggerLabel}
        </Button>
      </Badge>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { width: 320 } } }}
      >
        <Box sx={{ position: 'relative' }}>
          {loading ? (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: `rgba(0, 0, 0, ${scrim.loading})`,
                zIndex: 1,
              }}
            >
              <CircularProgress size={24} />
            </Box>
          ) : null}

          <Box sx={{ p: 2, maxHeight: 420, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
            {filters.map((def) => {
              const options = visibleOptions(def);
              const selected = getSelection(def.key).values;
              const allVisibleSelected = options.length > 0 && options.every((o) => selected.includes(o.value));
              return (
                <Box key={def.key}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {def.label}
                  </Typography>
                  {searchable ? (
                    <Box sx={{ mb: 1 }}>
                      <Search
                        placeholder={`Search ${def.label.toLowerCase()}...`}
                        onChange={(q) => {
                          setQueries((prev) => ({ ...prev, [def.key]: q }));
                          async?.onSearch?.(def.key, q);
                        }}
                      />
                    </Box>
                  ) : null}
                  {showSelectAll && def.type !== 'select' ? (
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={allVisibleSelected}
                          indeterminate={!allVisibleSelected && options.some((o) => selected.includes(o.value))}
                          onChange={() => toggleSelectAll(def, options)}
                        />
                      }
                      label={<Typography variant="body1">Select all</Typography>}
                    />
                  ) : null}
                  <Stack>
                    {options.map((option) => (
                      <FormControlLabel
                        key={option.value}
                        control={
                          <Checkbox
                            size="small"
                            checked={selected.includes(option.value)}
                            onChange={() => toggleValue(def, option.value)}
                          />
                        }
                        label={<Typography variant="body1">{option.label}</Typography>}
                      />
                    ))}
                  </Stack>
                  {async?.hasMore ? (
                    <Button size="small" variant="ghost" onClick={() => async.onLoadMore?.(def.key)}>
                      Load more
                    </Button>
                  ) : null}
                </Box>
              );
            })}
          </Box>

          <Divider />
          <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end', p: 1.5 }}>
            <Button variant="ghost" size="small" onClick={reset}>
              Reset
            </Button>
            <Button variant="primary" size="small" onClick={apply} disabled={loading}>
              Apply
            </Button>
          </Stack>
        </Box>
      </Popover>
    </>
  );
}
